import React, { useMemo } from "react";

const GrievanceSummary = ({ grievances }) => {
  const constituencyData = JSON.parse(localStorage.getItem("constituencyData"));

  const stats = useMemo(() => {
    if (!grievances || grievances.length === 0) return null;

    const total = grievances.length;

    const priorityCounts = grievances.reduce((acc, g) => {
      const p = g.properties?.priority?.toLowerCase() || "unknown";
      acc[p] = (acc[p] || 0) + 1;
      return acc;
    }, {});

    const offices = new Set(
      grievances.map((g) => g.properties?.officeId).filter(Boolean)
    ).size;

    const genderCounts = grievances.reduce((acc, g) => {
      const gender = g.properties?.gender?.toLowerCase() || "unknown";
      acc[gender] = (acc[gender] || 0) + 1;
      return acc;
    }, {});

    // --- Average weekly footfall (adaptive rounding rule) ---
    const dates = grievances.map((g) => new Date(g.properties?.createdAt));
    const minDate = new Date(Math.min(...dates));
    const today = new Date();

    const days = (today - minDate) / (1000 * 60 * 60 * 24);
    const weeksFloat = days / 7;

    let weeksElapsed;
    if (weeksFloat < 1.5) {
      weeksElapsed = Math.floor(weeksFloat);
    } else {
      weeksElapsed = Math.ceil(weeksFloat);
    }

    // clamp to at least 1 week
    weeksElapsed = Math.max(1, weeksElapsed);

    const avgFootfall = Math.round(total / weeksElapsed);

    return { total, priorityCounts, offices, genderCounts, avgFootfall, weeksElapsed };
  }, [grievances]);

  if (!stats) return null;

  return (
    <div className="absolute top-4 left-4 bg-glass backdrop-blur-[40px] border border-white/70 rounded-3xl shadow-lg p-4 w-80">
      {/* Header */}
      <div className="text-center mb-4">
        <div className="text-lg font-bold text-[#004877]">Grievance Summary</div>
        <div className="text-3xl font-extrabold text-[#004877]">{stats.total}</div>
        <div className="text-xs text-gray-600">Total Grievances</div>

        {/* Constituency Info */}
        {constituencyData && (
          <div className="mt-1 text-xs font-semibold text-gray-700">
            {constituencyData.data.name} • {constituencyData.data.district}
          </div>
        )}
      </div>

      {/* Priority */}
      <div className="mb-3 shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
                relative overflow-hidden rounded-xl border backdrop-blur-xl
                border-white/30 bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50 p-2 flex flex-col items-center">
        <div className="text-sm font-semibold text-[#004877] mb-1">By Priority</div>
        <div className="flex gap-2 text-xs justify-center">
          <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full">
            High: {stats.priorityCounts.high || 0}
          </span>
          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full">
            Medium: {stats.priorityCounts.medium || 0}
          </span>
          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full">
            Low: {stats.priorityCounts.low || 0}
          </span>
        </div>
      </div>

      {/* Offices */}
      <div className="mb-3 shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
                relative overflow-hidden rounded-xl border backdrop-blur-xl
                border-white/30 bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50 p-2 flex flex-col items-center">
        <div className="text-sm font-semibold text-[#004877]">No. of Offices</div>
        <div className="text-lg font-bold text-center text-[#004877]">{stats.offices}</div>
      </div>

      {/* Gender */}
      <div className="mb-3 shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
                relative overflow-hidden rounded-xl border backdrop-blur-xl
                border-white/30 bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50 p-2 flex flex-col items-center">
        <div className="text-sm font-semibold text-[#004877]">Gender-wise</div>
        <div className="flex gap-3 text-xs justify-center">
          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
            Male: {stats.genderCounts.male || 0}
          </span>
          <span className="px-2 py-1 bg-pink-100 text-pink-800 rounded-full">
            Female: {stats.genderCounts.female || 0}
          </span>
          <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full">
            Other: {stats.genderCounts.other || 0}
          </span>
        </div>
      </div>

      {/* Footfall */}
      <div className="shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
                relative overflow-hidden rounded-xl border backdrop-blur-xl
                border-white/30 bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50 p-2 flex flex-col items-center">
        <div className="text-sm font-semibold text-[#004877]">Avg. Weekly Footfall</div>
        <div className="text-lg font-bold text-[#004877]">
          {stats.avgFootfall}
        </div>
      </div>
    </div>
  );
};

export default GrievanceSummary;
